'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalculatorInputs {
  monthlyLeads: number;
  conversionRate: number;
  averageDealValue: number;
  marketingBudget: number;
}

interface CalculatorResults {
  currentRevenue: number;
  projectedRevenue: number;
  revenueIncrease: number;
  roi: number;
  paybackPeriod: number;
}

const ROICalculator = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [inputs, setInputs] = useState<CalculatorInputs>({
    monthlyLeads: 500,
    conversionRate: 2,
    averageDealValue: 5000,
    marketingBudget: 10000
  });
  const [results, setResults] = useState<CalculatorResults>({
    currentRevenue: 0,
    projectedRevenue: 0,
    revenueIncrease: 0,
    roi: 0,
    paybackPeriod: 0
  });
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const calculateROI = () => {
    const currentMonthlyRevenue = inputs.monthlyLeads * (inputs.conversionRate / 100) * inputs.averageDealValue;
    const projectedMonthlyRevenue = inputs.monthlyLeads * 1.5 * ((inputs.conversionRate * 2.34) / 100) * inputs.averageDealValue;
    const monthlyIncrease = projectedMonthlyRevenue - currentMonthlyRevenue;
    const annualIncrease = monthlyIncrease * 12;
    const implementationCost = 25000;
    const roiPercentage = ((annualIncrease - implementationCost) / implementationCost) * 100;
    const payback = implementationCost / monthlyIncrease;

    setResults({
      currentRevenue: currentMonthlyRevenue,
      projectedRevenue: projectedMonthlyRevenue,
      revenueIncrease: monthlyIncrease,
      roi: roiPercentage,
      paybackPeriod: payback
    });
    setShowResults(true);
  };

  const handleInputChange = (field: keyof CalculatorInputs, value: string) => {
    const numValue = parseFloat(value) || 0;
    setInputs(prev => ({ ...prev, [field]: numValue }));
    setShowResults(false);
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (!isHydrated) {
    return (
      <section className="py-20 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card rounded-2xl p-8 shadow-elevated">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-1/3"></div>
              <div className="h-4 bg-muted rounded w-2/3"></div>
              <div className="grid md:grid-cols-2 gap-8 mt-8">
                <div className="space-y-4">
                  <div className="h-20 bg-muted rounded"></div>
                  <div className="h-20 bg-muted rounded"></div>
                </div>
                <div className="h-64 bg-muted rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-card px-4 py-2 rounded-full mb-4">
            <Icon name="CalculatorIcon" size={20} className="text-primary" />
            <span className="text-sm font-semibold text-primary">ROI Calculator</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            Calculate Your Marketing ROI
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            See the measurable impact of AI-powered marketing automation on your bottom line with industry benchmarks and competitive analysis.
          </p>
        </div>

        <div className="bg-card rounded-2xl p-8 shadow-elevated">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h3 className="text-xl font-heading font-bold text-foreground mb-6">
                Enter Your Current Metrics
              </h3>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Monthly Leads
                </label>
                <input
                  type="number"
                  value={inputs.monthlyLeads}
                  onChange={(e) => handleInputChange('monthlyLeads', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  placeholder="500"
                />
                <p className="text-xs text-muted-foreground mt-1">Average number of leads per month</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Conversion Rate (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={inputs.conversionRate}
                  onChange={(e) => handleInputChange('conversionRate', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  placeholder="2.0"
                />
                <p className="text-xs text-muted-foreground mt-1">Percentage of leads that convert to customers</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Average Deal Value ($)
                </label>
                <input
                  type="number"
                  value={inputs.averageDealValue}
                  onChange={(e) => handleInputChange('averageDealValue', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  placeholder="5000"
                />
                <p className="text-xs text-muted-foreground mt-1">Average revenue per customer</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Monthly Marketing Budget ($)
                </label>
                <input
                  type="number"
                  value={inputs.marketingBudget}
                  onChange={(e) => handleInputChange('marketingBudget', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  placeholder="10000"
                />
                <p className="text-xs text-muted-foreground mt-1">Current monthly marketing spend</p>
              </div>

              <button
                onClick={calculateROI}
                className="w-full px-6 py-4 bg-accent text-accent-foreground font-heading font-semibold rounded-lg hover:bg-accent/90 hover:shadow-elevated transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <Icon name="CalculatorIcon" size={20} />
                <span>Calculate ROI</span>
              </button>
            </div>

            <div>
              {showResults ? (
                <div className="space-y-6">
                  <h3 className="text-xl font-heading font-bold text-foreground mb-6">
                    Your Projected Results
                  </h3>

                  <div className="bg-gradient-to-br from-success/10 to-success/5 rounded-xl p-6 border border-success/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-muted-foreground">Current Monthly Revenue</span>
                      <Icon name="TrendingUpIcon" size={20} className="text-success" />
                    </div>
                    <p className="text-3xl font-heading font-bold text-foreground">
                      {formatCurrency(results.currentRevenue)}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-6 border border-primary/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-muted-foreground">Projected Monthly Revenue</span>
                      <Icon name="ChartBarIcon" size={20} className="text-primary" />
                    </div>
                    <p className="text-3xl font-heading font-bold text-foreground">
                      {formatCurrency(results.projectedRevenue)}
                    </p>
                    <p className="text-sm text-success font-semibold mt-2">
                      +{formatCurrency(results.revenueIncrease)} increase
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-accent/10 rounded-xl p-4 border border-accent/20">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name="CurrencyDollarIcon" size={18} className="text-accent" />
                        <span className="text-xs font-semibold text-muted-foreground">Annual ROI</span>
                      </div>
                      <p className="text-2xl font-heading font-bold text-foreground">
                        {results.roi.toFixed(0)}%
                      </p>
                    </div>

                    <div className="bg-success/10 rounded-xl p-4 border border-success/20">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name="ClockIcon" size={18} className="text-success" />
                        <span className="text-xs font-semibold text-muted-foreground">Payback Period</span>
                      </div>
                      <p className="text-2xl font-heading font-bold text-foreground">
                        {results.paybackPeriod.toFixed(1)} mo
                      </p>
                    </div>
                  </div>

                  <div className="bg-muted rounded-xl p-6">
                    <h4 className="text-sm font-heading font-bold text-foreground mb-4 uppercase tracking-wide">
                      Industry Benchmarks
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Lead Generation Increase</span>
                        <span className="text-sm font-semibold text-success">+150% avg</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Conversion Rate Improvement</span>
                        <span className="text-sm font-semibold text-success">+234% avg</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Marketing Cost Reduction</span>
                        <span className="text-sm font-semibold text-success">-45% avg</span>
                      </div>
                    </div>
                  </div>

                  <button className="w-full px-6 py-4 bg-primary text-primary-foreground font-heading font-semibold rounded-lg hover:bg-primary/90 hover:shadow-elevated transition-all duration-300 flex items-center justify-center space-x-2">
                    <Icon name="DocumentArrowDownIcon" size={20} />
                    <span>Download Full Report</span>
                  </button>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center bg-muted/50 rounded-xl p-12">
                  <div className="text-center space-y-4">
                    <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                      <Icon name="CalculatorIcon" size={40} className="text-primary" />
                    </div>
                    <p className="text-lg font-semibold text-muted-foreground">
                      Enter your metrics and click Calculate ROI to see your projected results
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;